<template>
  <BasePage title="Chits" subtitle="Manage chits information">
    <template #page-header:actions>
      <v-btn
        color="primary"
        depressed
        v-if="currentUser?.hasPermission('chits.add_chit')"
        @click="openChitFormDialog()"
      >
        <template #prepend>
          <v-icon start class="hidden-sm-and-down me-0"> {{ route.meta.icon }} </v-icon>
        </template>
        Create Chit
      </v-btn>

      <v-btn
        class="ml-2"
        color="secondary"
        depressed
        :loading="exportInProgress"
        @click="downloadChitExcel()"
      >
        Export Chits
      </v-btn>
    </template>

    <template #page-header:bottom>
      <ErrorAlert
        v-if="error != null"
        dense
        :error="error"
        @clearErrors="clearErrors"
        class="mb-0 mt-4 rounded"
      />
    </template>

    <v-card flat height="calc(100vh - 286px)" class="px-4 rounded-lg">
      <v-toolbar flat height="84" color="transparent">
        <ChitSearchBox
          v-model:search="currentSearch"
          @update:search="
            (newSearch: string) =>
              debounceFilterChits({ ...chitFilters, page: 1, search: newSearch })
          "
          :filters="chitFilters"
          @filter-applied="(newFilter) => updateFilters(chitFilters, newFilter)"
        />
      </v-toolbar>

      <ChitList
        v-model:page="currentPage"
        :loading="isTableLoading"
        :count="count"
        :chits="chits ?? []"
        @export-chit-pdf-pressed="downloadChitPdf"
        @chit-create-pressed="openChitFormDialog()"
        @edit-chit-pressed="openChitFormDialog"
        @delete-chit-pressed="openDeleteChitDialog"
        @chit-selected="goToChitDetail"
      />
    </v-card>

    <ConfirmationDialog
      v-if="selectedChit"
      width="450"
      title="Delete chit"
      v-model="deleteDialog"
      v-model:error="deleteError"
    >
      <template #message>
        Are you sure you want to delete
        <span class="text-medium-high-emphasis font-weight-bold">{{
          selectedChit.chit_number
        }}</span
        >?
        <br />
        <div class="pt-2">This action cannot be undone.</div>
      </template>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn
          color="error"
          variant="flat"
          :disabled="deleteInProgress"
          :loading="deleteInProgress"
          @click="removeChit(selectedChit)"
        >
          Delete
        </v-btn>
      </template>
    </ConfirmationDialog>
  </BasePage>

  <ChitFormDialog
    v-model:dialog="isChitFormDialogActive"
    :chit="selectedChit!"
    :isEdit="!!selectedChit"
    :disable-chit-picker="false"
    @saved-chit="filterChits"
    ref="chitFormDialogRef"
  />
</template>

<script lang="ts" setup>
import { debounce, merge } from 'lodash'
import { storeToRefs } from 'pinia'
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BasePage from '@/components/base/BasePage.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'
import ChitSearchBox from '@/components/chits/ChitSearchBox.vue'
import ChitList from '@/components/chits/ChitList.vue'

import type { IChitFilterParam } from '@/services'
import {
  useFetchChits,
  useDeleteChit,
  useExportChitExcel,
  useExportChitPdf
} from '@/composables/chit'

import type { IChit } from '@/models/chit'
import { useAuthStore } from '@/stores'
import { updateFilters } from '@/utils/filters'
import { format } from 'date-fns'
import { createFile } from '@/utils/helpers'
import type { ISystemError } from '@/models/error'

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import { DebounceDelay } from '@/utils/constants'
import { useQueryClient } from '@tanstack/vue-query'
import ChitFormDialog from '@/components/chits/ChitFormDialog.vue'

const route = useRoute()
const router = useRouter()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const chitFilters = reactive<IChitFilterParam>({
  search: route.query.search as string,
  page: route.query.page ? Number(route.query.page) : 1,
  state: route.query.state ? Number(route.query.state) : undefined,
  site: route.query.site ? Number(route.query.site) : undefined,
  client: route.query.client ? Number(route.query.client) : undefined,
  created_by: route.query.created_by ? Number(route.query.created_by) : undefined,
  received_at_after: route.query.received_at_after as string,
  received_at_before: route.query.received_at_before as string,
  arrived_at_after: route.query.arrived_at_after as string,
  arrived_at_before: route.query.arrived_at_before as string,
  departed_at_after: route.query.departed_at_after as string,
  departed_at_before: route.query.departed_at_before as string,
  created_after: route.query.created_after as string,
  created_before: route.query.created_before as string,
  modified_after: route.query.modified_after as string
})

const currentSearch = ref<string>(chitFilters.search ?? '')

const selectedChit = ref<IChit>()

watch(
  chitFilters,
  (value) => {
    /* this will update the URL query based on param values. */
    router
      .replace({
        name: route.name!,
        query: value as Record<string, any>
      })
      .catch((e: Error) => e) //catch navigation duplication error
  },
  {
    immediate: true
  }
)

const currentPage = computed({
  get() {
    return Number(chitFilters.page!)
  },
  set(page: number) {
    chitFilters.page = page
  }
})

const isChitFormDialogActive = ref(false)
function openChitFormDialog(chit?: IChit) {
  selectedChit.value = chit

  isChitFormDialogActive.value = true
}

const deleteDialog = ref(false)

function openDeleteChitDialog(chit: IChit) {
  clearDeleteErrors()
  deleteDialog.value = true
  selectedChit.value = chit
}

function closeDeleteDialog() {
  deleteDialog.value = false
}

// CORE
const queryClient = useQueryClient()

const { chits, count, isLoading, error: fetchError, queryKey } = useFetchChits(chitFilters)

function filterChits() {
  queryClient.fetchQuery({ queryKey: queryKey.value })
}

// debouncing
const debounceFilterChits = debounce(function (filterParams: IChitFilterParam) {
  // mutates filters
  merge(chitFilters, filterParams)
  queryClient.invalidateQueries({ queryKey: queryKey.value })
}, DebounceDelay.Long)

const {
  isPending: deleteInProgress,
  error: deleteError,
  mutate: deleteChit,
  reset: clearDeleteErrors
} = useDeleteChit()

function removeChit(chit: IChit) {
  deleteChit(chit.chit_number!, {
    onSuccess: () => {
      closeDeleteDialog()
    }
  })
}

function clearErrors() {
  clearExportErrors()
  error.value = null
}

function goToChitDetail(chit: IChit) {
  router.push({ name: 'chit-detail', params: { id: chit.chit_number } })
}

const error = ref<ISystemError | null>(null)

const isTableLoading = computed(() => isLoading.value || exportPdfInProgress.value)

const {
  isPending: exportInProgress,
  error: exportError,
  mutate: exportChitExcelMutate,
  reset: clearExportErrors
} = useExportChitExcel()

function downloadChitExcel() {
  clearExportErrors()
  exportChitExcelMutate(chitFilters, {
    onSuccess: (blob: Blob) => {
      createFile(blob, 'chit-reports', 'xlsx')
    }
  })
}

const {
  isPending: exportPdfInProgress,
  error: exportPdfError,
  mutate: exportChitPdfMutate,
  reset: clearExportPdfErrors
} = useExportChitPdf()

function downloadChitPdf(chit: IChit) {
  clearExportPdfErrors()
  exportChitPdfMutate(chit.chit_number!, {
    onSuccess: (blob: Blob) => {
      const fileName = `chit-report-${chit.chit_number}-${format(Date.now(), 'dd-MM-yy hh:mm a')}.pdf`
      createFile(blob, fileName, 'pdf')
    }
  })
}

watch(
  [fetchError, exportError, exportPdfError],
  ([newFetchError, newExportError, newExportPdfError]) => {
    error.value = newFetchError || newExportError || newExportPdfError
  }
)
</script>

<style lang="scss">
.site-list .v-list-item--three-line .v-list-item-subtitle {
  -webkit-line-clamp: 5;
}

.site-subheader {
  .v-list-subheader__text {
    align-items: center;
    flex-grow: 1;
    display: flex;
    flex-direction: row;
  }
}
</style>
